// Долги человека в карточке подотчётного лица: свод и переход туда, где с ними работают.
//
// ## Зачем блок появился
//
// Заказчик 21.09.2026: «нажимаем на сотрудника, а долгов по нему нету». Он прав по сути и не
// по адресу. Подотчёт и долг — разные вещи: подотчёт это деньги компании НА РУКАХ у человека,
// за которые он отчитывается чеком, долг — это сколько человек ДОЛЖЕН компании за форму,
// билет, обучение. Живут они в разных разделах, и это правильно.
//
// Неправильным было молчание. Человек открывал карточку, видел пять вкладок про движение
// денег и не мог понять, долгов у этого человека нет — или их тут просто не показывают. Это
// два разных факта, и разница между ними стоит решения: удерживать при расчёте или нет.
//
// ## Почему блок, а не шестая вкладка
//
// Вкладка означала бы ТРЕТЬЮ реализацию одного и того же: реестр долгов в «Расчёте» отвечает
// на вопрос «сколько висит на всех», вкладка карточки человека — «сколько на нём», и заводить
// рядом третью поверхность с теми же строками правила проекта прямо запрещают. Здесь нужен
// ответ, а не рабочее место: сумма, сколько долгов, чем они вызваны — и один переход туда,
// где их заводят, правят и списывают.
//
// ## Сумма берётся ТЕМ ЖЕ запросом, что и на вкладке
//
// Хук общий (`shared/debts/queries`). Свой запрос здесь означал бы второй ключ кэша на один
// ответ, то есть две суммы долга у одного человека на соседних экранах — расходящиеся ровно
// тогда, когда одну сбросили, а другую нет.

import { Link } from 'react-router';

import { RiArrowRightSLine } from '@remixicon/react';

import { useEmployeeDebts } from '@/shared/debts/queries';
import { ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ, useПереносДолгов, состояниеПереноса } from '@/shared/debts/перенос';
import { formatKopecks } from '@/shared/money/format';
import { countLabel } from '@/shared/text/plural';
import { Block } from '@/shared/ui/block';
import { Metric, MetricRow } from '@/shared/ui/metric';
import { Skeleton } from '@/shared/ui/screen-state';

/** Сколько строк долга показывать здесь. Дальше — в карточке человека, где их и разбирают. */
const ПОКАЗЫВАЕМ = 3;

export function ДолгиСотрудника({
  employeeId,
  employeeName,
}: {
  employeeId: string;
  employeeName: string;
}) {
  const долги = useEmployeeDebts(employeeId);
  // 23.09.2026 этот блок писал «ничего не должен» у человека, у которого долги были: их просто
  // не переносили в программу. Пустота называется тем, что правда известно.
  const перенос = состояниеПереноса(useПереносДолгов());
  const адрес = `/people/employees/r/${employeeId}?tab=debts`;

  const переход = (
    <Link
      to={адрес}
      // Двенадцать точек шрифта дают ссылку в шестнадцать точек высотой, а палец на планшете
      // ловит сорок четыре. Вид не меняется: `цель-тача` рисует невидимый прямоугольник вокруг
      // и включается только под палец (замер 01.10.2026: эта ссылка стояла на шести экранах
      // карточки подотчётного лица — сама карточка, три мастера, отчёт по чеку и настройки).
      className='цель-тача relative flex items-center gap-0.5 text-paragraph-xs text-primary-base hover:underline'
    >
      Открыть карточку
      <RiArrowRightSLine className='size-4' />
    </Link>
  );

  if (долги.isPending) {
    return (
      <Block title='Долги сотрудника' action={переход}>
        <Skeleton className='h-10 w-full' />
      </Block>
    );
  }

  if (долги.error !== null) {
    // Ошибку этого блока НЕ раздуваем до состояния экрана: карточка про подотчёт, и она
    // обязана работать, даже когда реестр долгов не отвечает. Но и молчать нельзя — иначе
    // «долгов нет» и «мы не спросили» слипаются в одно.
    return (
      <Block title='Долги сотрудника' action={переход}>
        <p className='text-paragraph-sm text-text-sub-600'>
          Долги сейчас не приехали: {долги.error.envelope.message}. Посмотреть их можно в карточке
          человека.
        </p>
      </Block>
    );
  }

  const действующие = (долги.data?.items ?? []).filter((долг) => долг.outstanding > 0);
  const всего = действующие.reduce((сумма, долг) => сумма + долг.outstanding, 0);

  if (действующие.length === 0) {
    return (
      <Block title='Долги сотрудника' action={переход}>
        {/* Ноль назван СЛОВОМ, а не цифрой на пустом месте: «0 ₽» здесь читается как
            «посчитали и вышло ноль», а сказать надо «долгов нет ни одного». Ровно на этой
            разнице заказчик и споткнулся — он не понял, их нет или их не показывают. */}
        <p className='text-paragraph-sm text-text-sub-600'>
          {перенос === 'перенесены'
            ? `Действующих долгов нет: за форму, билет и обучение ${employeeName} ничего не должен.`
            : перенос === 'не перенесены'
              ? `В программе долгов у ${employeeName} нет. ${ДОЛГИ_НЕ_ПЕРЕНЕСЕНЫ} Поэтому это не значит, что он ничего не должен.`
              : `В программе действующих долгов у ${employeeName} нет.`}
        </p>
      </Block>
    );
  }

  return (
    <Block title='Долги сотрудника' action={переход}>
      <div className='flex flex-col gap-3'>
        <MetricRow columns={2}>
          <Metric
            label='Всего должен'
            value={formatKopecks(всего)}
            tone='важный'
            note={`${countLabel(действующие.length, ['действующий долг', 'действующих долга', 'действующих долгов'])}`}
          />
          {/* Блокирующий долг решает, можно ли уволить человека и закрыть расчёт. Показываем
              только когда он есть: строка «блокирующих нет» рядом с нулём — это второй
              пересказ того же факта. */}
          {действующие.some((долг) => долг.is_blocking) && (
            <Metric
              label='Из них держат расчёт'
              value={String(действующие.filter((долг) => долг.is_blocking).length)}
              tone='тревожный'
              note='без решения по ним расчёт не закрыть'
            />
          )}
        </MetricRow>

        <ul className='flex flex-col divide-y divide-stroke-soft-200'>
          {действующие.slice(0, ПОКАЗЫВАЕМ).map((долг) => (
            <li key={долг.id} className='flex items-center justify-between gap-3 py-2'>
              <div className='flex min-w-0 flex-col'>
                <span className='truncate text-paragraph-sm text-text-strong-950'>
                  {долг.debt_type_title}
                </span>
                <span className='truncate text-paragraph-xs text-text-sub-600'>
                  {долг.schedule_note}
                </span>
              </div>
              {/* Знака рубля нет: столбец целиком денежный, единицу назвала величина выше. */}
              <span className='shrink-0 tabular-nums text-label-sm text-text-strong-950'>
                {formatKopecks(долг.outstanding)}
              </span>
            </li>
          ))}
        </ul>

        {действующие.length > ПОКАЗЫВАЕМ && (
          <Link to={адрес} className='text-paragraph-xs text-primary-base hover:underline'>
            Ещё {действующие.length - ПОКАЗЫВАЕМ} — в карточке человека
          </Link>
        )}
      </div>
    </Block>
  );
}
